import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, updateDoc, deleteDoc, addDoc, getDoc, writeBatch } from 'firebase/firestore';
import { ChevronDown, ChevronUp, Clock, User, Plus, Calendar, Check, Loader, AlertCircle, Edit2, Trash2, Move, RotateCcw } from 'lucide-react';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { db as firebaseDb } from '../firebase';
import ClientEditAppointmentModal from './ClientEditAppointmentModal';
import ClientAddAppointmentModal from './ClientAddAppointmentModal';
import RecurrenceMoveModal from './RecurrenceMoveModal';
import AppointmentViewModal from './AppointmentViewModal';
import { useTheme } from '../contexts/ThemeContext';
import { generateRecurringAppointments } from '../utils/recurrenceUtils';
import '../ClientDayViewMobile.css';

// Fonction utilitaire pour comparer deux dates
const isSameDay = (date1, date2) => {
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === d2.getDate();
};

const ClientDayViewMobile = React.memo(({ db, client, currentDate, formatDate, formatTime, hasPermission }) => {
  const safeDb = db || firebaseDb;
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    unassigned: true,
    employees: {}
  });
  const [showRecurrenceMoveModal, setShowRecurrenceMoveModal] = useState(false);
  const [pendingMoveData, setPendingMoveData] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);

  const { theme } = useTheme();
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Gestion robuste des permissions avec hasPermission
  const canEditAppointments = hasPermission ? hasPermission({ resource: 'appointments', action: 'write' }) : false;

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      // Récupérer tous les employés sans filtre isArchived côté Firestore
      const querySnapshot = await getDocs(employeesRef);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080',
        photoURL: doc.data().photoURL || null
      }));

      // Filtrer côté client pour inclure les employés où isArchived est false ou absent
      const filteredEmployees = fetchedEmployees.filter(employee => {
        // Exclure les comptes bénéficiaires (rôle 'beneficiaire')
        if (employee.role === 'beneficiaire') {
          return false;
        }

        // Inclure si isArchived est false ou absent/undefined
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;

        return isNotArchived && isActive;
      });

      setEmployees(filteredEmployees);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des employés
    }
  }, [db]);

  const fetchAppointments = useCallback(async () => {
    if (!db || !client.id) return;
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('clientId', '==', client.id),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay))
      );
      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));
      setAppointments(fetchedAppointments);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous
    }
  }, [db, client.id, currentDate]);

  const fetchApprovedLeaves = useCallback(async () => {
    if (!db) return;
    try {
      const leavesRef = collection(db, 'leaves');
      const q = query(leavesRef, where('status', '==', 'approved'));
      const querySnapshot = await getDocs(q);
      const leaves = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate()
      }));
      setApprovedLeaves(leaves);
    } catch (error) {
      // Erreur silencieuse lors de la récupération des congés
    }
  }, [db]);

  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
    fetchApprovedLeaves();
  }, [fetchEmployees, fetchAppointments, fetchApprovedLeaves, refreshTrigger]);

  // Fonction pour vérifier si un employé est en congé
  const isEmployeeOnLeave = useCallback((employeeId) => {
    if (!approvedLeaves.length) return false;

    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0);

    return approvedLeaves.some(leave => {
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return leave.employeeId === employeeId &&
        today >= startDate &&
        today <= endDate;
    });
  }, [approvedLeaves, currentDate]);

  // Gestion des sections dépliables
  const toggleSection = (section, employeeId = null) => {
    if (section === 'unassigned') {
      setExpandedSections(prev => ({
        ...prev,
        unassigned: !prev.unassigned
      }));
    } else if (section === 'employee' && employeeId) {
      setExpandedSections(prev => ({
        ...prev,
        employees: {
          ...prev.employees,
          [employeeId]: !prev.employees[employeeId]
        }
      }));
    }
  };

  // Gestion du menu d'actions
  const openActionMenu = (appointment, event) => {
    event.stopPropagation();
    setActionMenu({
      appointment,
      position: { x: event.clientX, y: event.clientY }
    });
  };

  const closeActionMenu = () => {
    setActionMenu(null);
  };

  // Fonctions CRUD
  const handleAddAppointment = async (appointmentData) => {
    if (!canEditAppointments) return;
    try {
      let appointmentsToAdd = [];
      if (appointmentData.isRecurring) {
        appointmentsToAdd = generateRecurringAppointments(appointmentData, safeDb);
      } else {
        appointmentsToAdd = [{
          ...appointmentData,
          isRecurring: false,
          status: appointmentData.employeeId ? 'assigne' : 'libre'
        }];
      }

      const batch = writeBatch(safeDb);

      appointmentsToAdd.forEach(appointment => {
        const newAppointmentRef = doc(collection(safeDb, 'appointments'));

        let employeeName = '';
        if (appointment.employeeId) {
          const employee = employees.find(emp => emp.userId === appointment.employeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        batch.set(newAppointmentRef, {
          ...appointment,
          clientId: client.id,
          employeeName: employeeName,
          date: Timestamp.fromDate(appointment.date)
        });
      });

      await batch.commit();

      setIsModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de l'ajout du/des rendez-vous
    }
  };

  const handleUpdateAppointment = async (updatedAppointment) => {
    if (!canEditAppointments) return;
    try {
      const appointmentRef = doc(db, 'appointments', updatedAppointment.id);
      const status = updatedAppointment.employeeId ? 'assigne' : 'libre';

      let employeeName = '';
      if (updatedAppointment.employeeId) {
        const employee = employees.find(emp => emp.userId === updatedAppointment.employeeId);
        if (employee) {
          employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
        }
      }

      await updateDoc(appointmentRef, {
        ...updatedAppointment,
        status: status,
        employeeName: employeeName
      });
      setIsModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la mise à jour du rendez-vous
    }
  };

  const handleDeleteAppointment = async (appointmentId, scope = 'this') => {
    if (!canEditAppointments) return;
    try {
      if (scope === 'this') {
        // Supprimer seulement ce rendez-vous
        await deleteDoc(doc(db, 'appointments', appointmentId));
      } else if (scope === 'future') {
        // Supprimer tous les rendez-vous futurs de la série
        const appointment = appointments.find(app => app.id === appointmentId);
        if (appointment && appointment.recurrenceSeriesId) {
          const appointmentsRef = collection(db, 'appointments');
          const q = query(
            appointmentsRef,
            where('clientId', '==', client.id),
            where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
          );
          const querySnapshot = await getDocs(q);

          const batch = writeBatch(db);
          const currentDate = new Date(appointment.date);

          querySnapshot.docs.forEach(doc => {
            const appData = doc.data();
            const appDate = appData.date?.toDate() || new Date();
            if (appDate >= currentDate) {
              batch.delete(doc.ref);
            }
          });

          await batch.commit();
        }
      }
      setIsModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la suppression du rendez-vous
    }
  };

  // Fonctions pour la gestion de la récurrence
  const generateRecurringAppointments = (appointmentData) => {
    const appointments = [];
    let currentDate = new Date(appointmentData.date);
    const endDate = getRecurrenceEndDate(appointmentData);

    const recurrenceSeriesId = doc(collection(db, 'appointments')).id;

    let occurrenceCount = 0;
    const maxOccurrences = appointmentData.recurrenceEndType === 'after' ? appointmentData.recurrenceEndAfter : Infinity;

    while (currentDate <= endDate && occurrenceCount < maxOccurrences) {
      if (appointmentData.recurrenceBase === 'daily') {
        appointments.push({
          ...appointmentData,
          date: new Date(currentDate),
          isRecurring: true,
          recurrenceSeriesId: recurrenceSeriesId
        });
        occurrenceCount++;
        currentDate.setDate(currentDate.getDate() + appointmentData.recurrenceFrequency);
      } else if (appointmentData.recurrenceBase === 'weekly') {
        if (appointmentData.recurrenceDay.includes(currentDate.getDay())) {
          appointments.push({
            ...appointmentData,
            date: new Date(currentDate),
            isRecurring: true,
            recurrenceSeriesId: recurrenceSeriesId
          });
          occurrenceCount++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
        if (currentDate.getDay() === 0) {
          currentDate.setDate(currentDate.getDate() + (7 * (appointmentData.recurrenceFrequency - 1)));
        }
      } else if (appointmentData.recurrenceBase === 'monthly') {
        if (shouldAddAppointment(currentDate, appointmentData)) {
          appointments.push({
            ...appointmentData,
            date: new Date(currentDate),
            isRecurring: true,
            recurrenceSeriesId: recurrenceSeriesId
          });
          occurrenceCount++;
        }
        currentDate = getNextRecurrenceDate(currentDate, appointmentData);
      }
    }

    return appointments;
  };

  const getRecurrenceEndDate = (appointmentData) => {
    if (appointmentData.recurrenceEndType === 'on' && appointmentData.recurrenceEndDate) {
      const endDate = new Date(appointmentData.recurrenceEndDate);
      endDate.setHours(23, 59, 59, 999);
      return endDate;
    } else if (appointmentData.recurrenceEndType === 'after') {
      const startDate = new Date(appointmentData.date);
      const endDate = new Date(startDate);
      if (appointmentData.recurrenceBase === 'daily') {
        endDate.setDate(startDate.getDate() + (appointmentData.recurrenceEndAfter - 1) * appointmentData.recurrenceFrequency);
      } else if (appointmentData.recurrenceBase === 'weekly') {
        endDate.setDate(startDate.getDate() + (appointmentData.recurrenceEndAfter - 1) * 7 * appointmentData.recurrenceFrequency);
      } else if (appointmentData.recurrenceBase === 'monthly') {
        endDate.setMonth(startDate.getMonth() + (appointmentData.recurrenceEndAfter - 1) * appointmentData.recurrenceFrequency);
      }
      return endDate;
    } else {
      const startDate = new Date(appointmentData.date);
      return new Date(startDate.getFullYear() + 1, startDate.getMonth(), startDate.getDate());
    }
  };

  const shouldAddAppointment = (date, appointmentData) => {
    if (appointmentData.recurrenceBase === 'monthly') {
      if (appointmentData.recurrenceMonthType === 'dayOfWeek') {
        const weekOfMonth = Math.floor((date.getDate() - 1) / 7) + 1;
        return date.getDay() === parseInt(appointmentData.recurrenceMonthWeekday, 10) &&
          weekOfMonth === parseInt(appointmentData.recurrenceMonthWeek, 10);
      } else {
        return date.getDate() === parseInt(appointmentData.recurrenceMonthDay, 10);
      }
    }
    return true;
  };

  const getNextRecurrenceDate = (date, appointmentData) => {
    let newDate = new Date(date);
    const frequency = parseInt(appointmentData.recurrenceFrequency, 10);

    if (appointmentData.recurrenceBase === 'monthly') {
      newDate.setMonth(newDate.getMonth() + frequency);

      if (appointmentData.recurrenceMonthType === 'dayOfWeek') {
        const desiredDay = parseInt(appointmentData.recurrenceMonthWeekday, 10);
        const desiredWeek = parseInt(appointmentData.recurrenceMonthWeek, 10);

        newDate.setDate(1);

        while (newDate.getDay() !== desiredDay) {
          newDate.setDate(newDate.getDate() + 1);
        }

        newDate.setDate(newDate.getDate() + (desiredWeek - 1) * 7);

        if (newDate.getMonth() > (date.getMonth() + frequency) % 12) {
          newDate = new Date(newDate.getFullYear(), newDate.getMonth(), 0);
        }
      } else {
        newDate.setDate(parseInt(appointmentData.recurrenceMonthDay, 10));
      }
    } else if (appointmentData.recurrenceBase === 'weekly') {
      newDate.setDate(newDate.getDate() + (7 * frequency));
    }

    return newDate;
  };

  // Fonction pour récupérer tous les rendez-vous d'une série récurrente
  const fetchAllRecurringAppointments = useCallback(async (appointment) => {
    if (!appointment.recurrenceSeriesId) return [];

    try {
      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('clientId', '==', client.id),
        where('recurrenceSeriesId', '==', appointment.recurrenceSeriesId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));
    } catch (error) {
      // Erreur silencieuse lors de la récupération des rendez-vous récurrents
      return [];
    }
  }, [db, client.id]);

  // Fonction pour mettre à jour un rendez-vous
  const updateAppointmentWithData = async (appointmentId, newEmployeeId, newStatus, employeeName) => {
    const updateData = {
      employeeId: newEmployeeId,
      status: newStatus,
      employeeName: employeeName
    };

    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        updateData[key] = null;
      }
    });

    try {
      await updateDoc(doc(db, 'appointments', appointmentId), updateData);
    } catch (error) {
      // Erreur silencieuse lors de la mise à jour du rendez-vous
      throw error;
    }
  };

  // Fonction pour gérer le déplacement d'une série de rendez-vous
  const handleRecurrenceMove = useCallback(async (scope, appointment, newEmployeeId, newStatus, employeeName, endDate = null) => {
    try {
      if (scope === 'this') {
        // Cette occurrence uniquement - briser la série
        await updateAppointmentWithData(appointment.id, newEmployeeId, newStatus, employeeName);

        // Supprimer l'ID de série pour briser la récurrence
        await updateDoc(doc(db, 'appointments', appointment.id), {
          recurrenceSeriesId: null,
          isRecurring: false
        });

      } else if (scope === 'future') {
        // Toutes les occurrences futures
        const allRecurringAppointments = await fetchAllRecurringAppointments(appointment);

        // Filtrer les occurrences futures (incluant celle-ci)
        const currentDate = new Date(appointment.date);
        currentDate.setHours(0, 0, 0, 0); // Normaliser l'heure pour la comparaison

        const appointmentsToUpdate = allRecurringAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0); // Normaliser l'heure pour la comparaison
          return appDate >= currentDate;
        });

        // Mettre à jour tous les rendez-vous sélectionnés
        for (const app of appointmentsToUpdate) {
          await updateAppointmentWithData(app.id, newEmployeeId, newStatus, employeeName);
        }

      } else if (scope === 'period' && endDate) {
        // Remplacement temporaire jusqu'à une date spécifique - DÉPLACEMENT au lieu de SUPPRESSION
        const allRecurringAppointments = await fetchAllRecurringAppointments(appointment);

        // Dates de la période de remplacement
        const currentDate = new Date(appointment.date);
        currentDate.setHours(0, 0, 0, 0);
        const endDateObj = new Date(endDate);
        endDateObj.setHours(23, 59, 59, 999);

        // Filtrer les rendez-vous à DÉPLACER (dans la période)
        const appointmentsToMove = allRecurringAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0);
          return appDate >= currentDate && appDate <= endDateObj;
        });

        // Créer une nouvelle série de remplacement
        const newSeriesId = doc(collection(db, 'appointments')).id;

        // DÉPLACER les rendez-vous vers la nouvelle série avec le nouvel intervenant
        const batch = writeBatch(db);
        for (const app of appointmentsToMove) {
          // Mettre à jour le rendez-vous existant avec le nouvel intervenant et la nouvelle série
          batch.update(doc(db, 'appointments', app.id), {
            employeeId: newEmployeeId,
            employeeName: employeeName,
            status: newStatus,
            recurrenceSeriesId: newSeriesId,
            // Conserver tous les autres paramètres (date, horaires, etc.)
          });
        }
        await batch.commit();
      }

      // Mettre à jour l'état local
      setAppointments(prevAppointments =>
        prevAppointments.map(app =>
          app.id === appointment.id
            ? {
              ...app,
              employeeId: newEmployeeId,
              status: newStatus,
              employeeName: employeeName
            }
            : app
        )
      );

      // Trigger a refresh
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors du déplacement de la série
    }
  }, [db, appointments, setRefreshTrigger, fetchAllRecurringAppointments]);

  // Fonction appelée quand l'utilisateur confirme le déplacement récurrent
  const handleRecurrenceMoveConfirm = useCallback(async (data) => {
    if (!pendingMoveData) {
      return;
    }

    const { appointment, newEmployeeId, newStatus, employeeName } = pendingMoveData;
    const { scope, endDate } = data;

    try {
      await handleRecurrenceMove(scope, appointment, newEmployeeId, newStatus, employeeName, endDate);
    } catch (error) {
      // Erreur silencieuse lors du déplacement récurrent
    }

    setShowRecurrenceMoveModal(false);
    setPendingMoveData(null);
  }, [pendingMoveData, handleRecurrenceMove]);

  // Fonction pour déplacer un rendez-vous vers un nouvel employé
  const handleMoveAppointment = async (appointment, newEmployeeId) => {
    if (!canEditAppointments) return;

    const newStatus = newEmployeeId ? 'assigne' : 'libre';

    let employeeName = '';
    if (newEmployeeId) {
      const employee = employees.find(emp => emp.userId === newEmployeeId);
      if (employee) {
        employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
      }
    }

    // Si le rendez-vous est récurrent, afficher le modal de choix
    if (appointment.isRecurring && appointment.recurrenceSeriesId) {
      setPendingMoveData({
        appointment,
        newEmployeeId,
        newStatus,
        employeeName
      });
      setShowRecurrenceMoveModal(true);
      return;
    }

    // Pour les rendez-vous non récurrents, procéder normalement
    try {
      await updateAppointmentWithData(appointment.id, newEmployeeId, newStatus, employeeName);

      setAppointments(prevAppointments =>
        prevAppointments.map(app =>
          app.id === appointment.id
            ? {
              ...app,
              employeeId: newEmployeeId,
              status: newStatus,
              employeeName: employeeName
            }
            : app
        )
      );

      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // Erreur silencieuse lors de la manipulation du rendez-vous
    }
  };

  // Fonction pour obtenir l'initiale d'un nom
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Fonction pour trier les rendez-vous par heure de début de manière chronologique
  const sortAppointments = (apps) => {
    return apps.sort((a, b) => {
      // Comparer d'abord les heures de début
      const timeComparison = a.startTime.localeCompare(b.startTime);
      if (timeComparison !== 0) return timeComparison;

      // Si les heures de début sont identiques, comparer les heures de fin
      return a.endTime.localeCompare(b.endTime);
    });
  };

  // Rendez-vous non assignés triés chronologiquement
  const unassignedAppointments = sortAppointments(
    appointments.filter(app => !app.employeeId || app.status === 'libre' || app.status === 'à combler')
  );

  // Fonction pour obtenir les rendez-vous d'un employé spécifique triés chronologiquement
  const getEmployeeAppointments = (employeeId) => {
    return sortAppointments(
      appointments.filter(app => app.employeeId === employeeId)
    );
  };


  // Composant pour un rendez-vous
  const AppointmentCard = ({ appointment }) => {
    const employee = employees.find(emp => emp.userId === appointment.employeeId);

    return (
      <div
        className={`p-3 rounded-lg border mb-2 ${isDarkMode
          ? 'bg-gray-800 border-gray-700 text-gray-100'
          : 'bg-white border-gray-200 text-gray-900'
          }`}
        onClick={() => {
          setSelectedAppointment(appointment);
          setIsViewModalOpen(true);
        }}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <Clock size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
              <span className="text-sm font-medium">
                {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
              </span>
            </div>
            {appointment.details && (
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {appointment.details}
              </div>
            )}
            {employee && (
              <div className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: employee.color }}
                ></div>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {employee.name || `${employee.firstName} ${employee.lastName}`}
                </span>
              </div>
            )}
          </div>
          {canEditAppointments && (
            <button
              onClick={(e) => openActionMenu(appointment, e)}
              className={`p-1 rounded ${isDarkMode
                ? 'hover:bg-gray-700 text-gray-400'
                : 'hover:bg-gray-100 text-gray-600'
                }`}
            >
              <Edit2 size={16} />
            </button>
          )}
        </div>
        {appointment.notes && appointment.notes.content && (
          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
            📝 Notes disponibles
          </div>
        )}
      </div>
    );
  };

  // Composant pour la section d'un employé
  const EmployeeSection = ({ employee }) => {
    const isExpanded = expandedSections.employees[employee.userId] !== false;
    const employeeAppointments = getEmployeeAppointments(employee.userId);
    const onLeave = isEmployeeOnLeave(employee.userId);

    return (
      <div className={`mb-4 rounded-lg border ${isDarkMode
        ? 'bg-gray-800 border-gray-700'
        : 'bg-white border-gray-200'
        }`}>
        <div
          className="p-3 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('employee', employee.userId)}
        >
          <div className="flex items-center space-x-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
              style={{ backgroundColor: employee.color }}
            >
              {employee.photoURL ? (
                <img
                  src={employee.photoURL}
                  alt={employee.name || `${employee.firstName} ${employee.lastName}`}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                getInitials(employee.name || `${employee.firstName} ${employee.lastName}`)
              )}
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {employee.name || `${employee.firstName} ${employee.lastName}`}
              </div>
              {onLeave && (
                <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center space-x-1">
                  <Clock size={12} />
                  <span>En congé</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {employeeAppointments.length > 0 && (
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode
                ? 'bg-gray-700 text-gray-300'
                : 'bg-gray-100 text-gray-600'
                }`}>
                {employeeAppointments.length}
              </span>
            )}
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {isExpanded && !onLeave && (
          <div className="px-3 pb-3">
            {employeeAppointments.length === 0 ? (
              <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                Aucun rendez-vous aujourd'hui
              </div>
            ) : (
              employeeAppointments.map(appointment => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            )}
          </div>
        )}

        {isExpanded && onLeave && (
          <div className="px-3 pb-3">
            <div className={`text-center py-4 text-sm rounded-lg ${isDarkMode
              ? 'bg-orange-900/20 text-orange-300'
              : 'bg-orange-50 text-orange-700'
              }`}>
              <Clock className="inline-block mr-2" size={16} />
              En congé aujourd'hui
            </div>
          </div>
        )}
      </div>
    );
  };

  // Composant pour la section des plages à combler
  const UnassignedSection = () => {
    const isExpanded = expandedSections.unassigned;

    return (
      <div className={`mb-4 rounded-lg border ${isDarkMode
        ? 'bg-gray-800 border-gray-700'
        : 'bg-white border-gray-200'
        }`}>
        <div
          className="p-3 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('unassigned')}
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-medium">
              ?
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Plages à combler
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Rendez-vous non assignés
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {unassignedAppointments.length > 0 && (
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode
                ? 'bg-gray-700 text-gray-300'
                : 'bg-gray-100 text-gray-600'
                }`}>
                {unassignedAppointments.length}
              </span>
            )}
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {isExpanded && (
          <div className="px-3 pb-3">
            {unassignedAppointments.length === 0 ? (
              <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                Aucune plage à combler aujourd'hui
              </div>
            ) : (
              unassignedAppointments.map(appointment => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Composant pour le menu d'actions
  const ActionMenu = () => {
    if (!actionMenu) return null;

    const { appointment, position } = actionMenu;

    // Calculer la hauteur maximale pour le menu
    const maxHeight = Math.min(window.innerHeight - position.y - 20, 300);
    const menuWidth = 200;

    return (
      <div
        className="fixed inset-0 z-50"
        onClick={closeActionMenu}
      >
        <div
          className={`absolute rounded-lg shadow-lg border overflow-hidden ${isDarkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
            }`}
          style={{
            left: Math.min(position.x, window.innerWidth - menuWidth - 10),
            top: Math.min(position.y, window.innerHeight - maxHeight - 10),
            width: menuWidth,
            maxHeight: maxHeight
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="overflow-y-auto" style={{ maxHeight: maxHeight - 10 }}>
            <button
              onClick={() => {
                setSelectedAppointment(appointment);
                setIsModalOpen(true);
                closeActionMenu();
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}
            >
              <Edit2 size={14} />
              <span>Modifier</span>
            </button>

            <button
              onClick={() => {
                handleDeleteAppointment(appointment.id);
                closeActionMenu();
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${isDarkMode ? 'text-red-400' : 'text-red-600'
                }`}
            >
              <Trash2 size={14} />
              <span>Supprimer</span>
            </button>

            {employees.length > 0 && (
              <div className="border-t border-gray-600 dark:border-gray-700">
                <div className={`px-3 py-2 text-xs font-medium ${isDarkMode ? 'text-gray-400 bg-gray-900' : 'text-gray-500 bg-gray-50'
                  }`}>
                  Déplacer vers:
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {employees.map(employee => (
                    <button
                      key={employee.userId}
                      onClick={() => {
                        handleMoveAppointment(appointment, employee.userId);
                        closeActionMenu();
                      }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}
                    >
                      <Move size={14} />
                      <span className="truncate">{employee.name || `${employee.firstName} ${employee.lastName}`}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      handleMoveAppointment(appointment, null);
                      closeActionMenu();
                    }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}
                  >
                    <Move size={14} />
                    <span>Plages à combler</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Composant pour le modal d'ajout/édition
  const AddAppointmentModal = ({ date, employees, onSave, onClose, initialEmployeeId }) => {
    const [appointmentData, setAppointmentData] = useState(() => {
      const initialDate = new Date(date);
      initialDate.setUTCHours(2, 0, 0, 0);

      // Si on a un rendez-vous sélectionné (modification), pré-remplir les données
      if (selectedAppointment) {
        const appointmentDate = selectedAppointment.date instanceof Date
          ? selectedAppointment.date
          : new Date(selectedAppointment.date);

        return {
          ...selectedAppointment,
          date: appointmentDate,
          startTime: selectedAppointment.startTime || '',
          endTime: selectedAppointment.endTime || '',
          employeeId: selectedAppointment.employeeId || '',
          status: selectedAppointment.employeeId ? 'assigne' : 'libre',
          isRecurring: selectedAppointment.isRecurring || false,
          recurrenceBase: selectedAppointment.recurrenceBase || 'weekly',
          recurrenceFrequency: selectedAppointment.recurrenceFrequency || 1,
          recurrenceDay: selectedAppointment.recurrenceDay || [appointmentDate.getDay()],
          recurrenceMonthDay: selectedAppointment.recurrenceMonthDay || appointmentDate.getDate(),
          recurrenceMonthType: selectedAppointment.recurrenceMonthType || 'dayOfMonth',
          recurrenceMonthWeek: selectedAppointment.recurrenceMonthWeek || Math.floor((appointmentDate.getDate() - 1) / 7) + 1,
          recurrenceMonthWeekday: selectedAppointment.recurrenceMonthWeekday || appointmentDate.getDay(),
          recurrenceEndType: selectedAppointment.recurrenceEndType || 'never',
          recurrenceEndAfter: selectedAppointment.recurrenceEndAfter || 1,
          recurrenceEndDate: selectedAppointment.recurrenceEndDate || null,
        };
      }

      // Sinon, créer un nouveau rendez-vous avec les valeurs par défaut
      return {
        date: initialDate,
        startTime: '',
        endTime: '',
        employeeId: initialEmployeeId || '',
        status: initialEmployeeId ? 'assigne' : 'libre',
        isRecurring: false,
        recurrenceBase: 'weekly',
        recurrenceFrequency: 1,
        recurrenceDay: [initialDate.getDay()],
        recurrenceMonthDay: initialDate.getDate(),
        recurrenceMonthType: 'dayOfMonth',
        recurrenceMonthWeek: Math.floor((initialDate.getDate() - 1) / 7) + 1,
        recurrenceMonthWeekday: initialDate.getDay(),
        recurrenceEndType: 'never',
        recurrenceEndAfter: 1,
        recurrenceEndDate: null,
      };
    });

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setAppointmentData(prev => {
        const newData = {
          ...prev,
          [name]: type === 'checkbox' ? checked :
            (name === 'recurrenceFrequency') ?
              Math.max(1, parseInt(value, 10) || 1) : value
        };

        if (name === 'employeeId') {
          newData.status = value ? 'assigne' : 'libre';
        }

        return newData;
      });
    };

    const handleDaySelection = (day) => {
      setAppointmentData(prev => ({
        ...prev,
        recurrenceDay: prev.recurrenceDay.includes(day)
          ? prev.recurrenceDay.filter(d => d !== day)
          : [...prev.recurrenceDay, day]
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      const dateWithTime = new Date(appointmentData.date);
      dateWithTime.setUTCHours(2, 0, 0, 0);
      onSave({
        ...appointmentData,
        date: dateWithTime,
        status: appointmentData.employeeId ? 'assigne' : 'libre'
      });
    };

    const getDayOfWeekName = (day) => {
      const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      return days[day];
    };

    const getWeekOfMonthName = (week) => {
      const weeks = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
      return weeks[Math.min(week - 1, 4)];
    };

    return (
      <EnhancedModal onClose={onClose} size="xl">
        <ModalHeader>{selectedAppointment ? 'Modifier' : 'Ajouter'} un rendez-vous</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section Informations de base */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold border-b pb-2 ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200'}`}>
                Informations de base
              </h3>

              {/* Date */}
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Calendar className="mr-2 text-blue-500" size={16} />
                  Date du rendez-vous
                </label>
                <input
                  type="date"
                  name="date"
                  value={appointmentData.date.toISOString().split('T')[0]}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                />
              </div>

              {/* Horaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="mr-2 text-blue-500" size={16} />
                    Heure de début
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={appointmentData.startTime}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                  />
                </div>

                <div className="space-y-2">
                  <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="mr-2 text-blue-500" size={16} />
                    Heure de fin
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={appointmentData.endTime}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                  />
                </div>
              </div>

              {/* Intervenant */}
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <User className="mr-2 text-blue-500" size={16} />
                  Intervenant assigné
                </label>
                <select
                  name="employeeId"
                  value={appointmentData.employeeId}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    }`}
                >
                  <option value="">Sélectionner un intervenant...</option>
                  {employees.map(emp => (
                    <option key={emp.userId} value={emp.userId}>
                      {emp.name || `${emp.firstName} ${emp.lastName}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Section Récurrence */}
            <div className="space-y-4">
              <div className={`flex items-center space-x-3 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                <div className={`p-2 rounded-full ${appointmentData.isRecurring
                  ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-600'
                  : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                  }`}>
                  <RotateCcw size={20} />
                </div>
                <div className="flex-1">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="isRecurring"
                      checked={appointmentData.isRecurring}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`w-10 h-6 rounded-full transition-colors duration-200 ease-in-out ${appointmentData.isRecurring ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                      }`}>
                      <div className={`bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${appointmentData.isRecurring ? 'translate-x-6' : 'translate-x-1'
                        } mt-1`} />
                    </div>
                    <span className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      Rendez-vous récurrent
                    </span>
                  </label>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Planifier ce rendez-vous de manière répétée selon une fréquence définie
                  </p>
                </div>
              </div>
            </div>

            {appointmentData.isRecurring && (
              <div className="space-y-4 ml-7">
                <div>
                  <select
                    name="recurrenceBase"
                    value={appointmentData.recurrenceBase}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="daily">Journalière</option>
                    <option value="weekly">Hebdomadaire</option>
                    <option value="monthly">Mensuelle</option>
                  </select>
                </div>

                {appointmentData.recurrenceBase === 'daily' && (
                  <div className="flex items-center space-x-2">
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Tous les</span>
                    <input
                      type="number"
                      name="recurrenceFrequency"
                      value={appointmentData.recurrenceFrequency || 1}
                      onChange={handleChange}
                      min="1"
                      className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>jour(s)</span>
                  </div>
                )}

                {appointmentData.recurrenceBase === 'weekly' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Tous les</span>
                      <input
                        type="number"
                        name="recurrenceFrequency"
                        value={appointmentData.recurrenceFrequency}
                        onChange={handleChange}
                        min="1"
                        className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>semaine(s)</span>
                    </div>
                  </>
                )}

                {appointmentData.recurrenceBase === 'monthly' && (
                  <>
                    <div>
                      <select
                        name="recurrenceMonthType"
                        value={appointmentData.recurrenceMonthType}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        <option value="dayOfMonth">Le même jour du mois</option>
                        <option value="dayOfWeek">Le même jour de la semaine</option>
                      </select>
                    </div>
                    {appointmentData.recurrenceMonthType === 'dayOfMonth' ? (
                      <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        Répéter le {appointmentData.recurrenceMonthDay} de chaque mois
                      </div>
                    ) : (
                      <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        Répéter le {getWeekOfMonthName(appointmentData.recurrenceMonthWeek)} {getDayOfWeekName(appointmentData.recurrenceMonthWeekday)} de chaque mois
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Tous les</span>
                      <input
                        type="number"
                        name="recurrenceFrequency"
                        value={appointmentData.recurrenceFrequency}
                        onChange={handleChange}
                        min="1"
                        className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>mois</span>
                    </div>
                  </>
                )}
                <div>
                  <select
                    name="recurrenceEndType"
                    value={appointmentData.recurrenceEndType}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    <option value="never">Jamais</option>
                    <option value="after">Après un nombre d'occurrences</option>
                    <option value="on">À une date spécifique</option>
                  </select>
                </div>
                {appointmentData.recurrenceEndType === 'after' && (
                  <div className="flex items-center space-x-2">
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Après</span>
                    <input
                      type="number"
                      name="recurrenceEndAfter"
                      value={appointmentData.recurrenceEndAfter}
                      onChange={handleChange}
                      min="1"
                      className={`w-16 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>occurrences</span>
                  </div>
                )}
                {appointmentData.recurrenceEndType === 'on' && (
                  <div>
                    <input
                      type="date"
                      name="recurrenceEndDate"
                      value={appointmentData.recurrenceEndDate || ''}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                )}
              </div>
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose}>
            Annuler
          </ModalButton>
          <ModalButton variant="primary" onClick={handleSubmit}>
            {selectedAppointment ? 'Mettre à jour' : 'Ajouter'}
          </ModalButton>
        </ModalFooter>
      </EnhancedModal>
    );
  };

  return (
    <div className="h-full overflow-auto pb-20">
      {/* En-tête */}
      <div className={`sticky top-0 z-10 p-4 border-b ${isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'
        }`}>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Planning du jour</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {formatDate(currentDate)}
            </p>
          </div>
          {canEditAppointments && (
            <button
              onClick={() => {
                setSelectedAppointment(null);
                setIsModalOpen(true);
              }}
              className={`p-3 rounded-full shadow-lg ${isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="p-4">
        {/* Section des plages à combler */}
        <UnassignedSection />

        {/* Sections des employés */}
        {employees.map(employee => (
          <EmployeeSection key={employee.userId} employee={employee} />
        ))}

        {/* Message si aucun employé */}
        {employees.length === 0 && (
          <div className={`text-center py-8 rounded-lg border ${isDarkMode
            ? 'bg-gray-800 border-gray-700 text-gray-400'
            : 'bg-gray-50 border-gray-200 text-gray-500'
            }`}>
            <User className="mx-auto mb-2 text-gray-400" size={32} />
            <p>Aucun intervenant disponible</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {isModalOpen && (
        <AddAppointmentModal
          date={currentDate}
          employees={employees}
          onSave={selectedAppointment ? handleUpdateAppointment : handleAddAppointment}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedAppointment(null);
          }}
          initialEmployeeId={selectedAppointment?.employeeId}
        />
      )}

      {/* Modal pour les déplacements de rendez-vous récurrents */}
      <RecurrenceMoveModal
        isOpen={showRecurrenceMoveModal}
        onClose={() => {
          setShowRecurrenceMoveModal(false);
          setPendingMoveData(null);
        }}
        onConfirm={handleRecurrenceMoveConfirm}
        appointment={pendingMoveData?.appointment}
      />

      {/* Menu d'actions */}
      <ActionMenu />

      {/* Modal de consultation des détails du rendez-vous */}
      {isViewModalOpen && (
        <AppointmentViewModal
          appointment={selectedAppointment}
          employees={employees}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedAppointment(null);
          }}
          onEdit={() => {
            setIsViewModalOpen(false);
            setIsModalOpen(true);
          }}
          onDelete={handleDeleteAppointment}
          hasPermission={hasPermission}
        />
      )}
    </div>
  );
});

// Ajouter le displayName pour le débogage
ClientDayViewMobile.displayName = 'ClientDayViewMobile';

export default ClientDayViewMobile;
